<template>
  <div class="social_contact_display">
    <static-page-loader v-if="loading" />

    <template v-else>
      <section v-if="visibleSocialLinks.length" class="contact_section">
        <div class="section_header">
          <i class="fas fa-share-alt section_icon"></i>
          <h3>{{ $t("STATIC_PAGES.socialMedia") }}</h3>
        </div>

        <div class="social_grid">
          <a
            v-for="item in visibleSocialLinks"
            :key="item.key"
            :href="item.url"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="item.label"
            class="social_card"
            :class="`social_card--${item.key}`"
          >
            <span class="social_card__icon">
              <i :class="item.icon"></i>
            </span>
            <span class="social_card__info">
              <span class="social_card__label">{{ item.label }}</span>
              <span class="social_card__hint">{{ $t("STATIC_PAGES.openLink") }}</span>
            </span>
            <i class="fas fa-external-link-alt social_card__arrow"></i>
          </a>
        </div>
      </section>

      <section v-if="visiblePhones.length" class="contact_section">
        <div class="section_header">
          <i class="fas fa-phone section_icon"></i>
          <h3>{{ $t("STATIC_PAGES.phoneNumbers") }}</h3>
        </div>

        <div class="phones_grid">
          <a
            v-for="(phone, index) in visiblePhones"
            :key="`phone-${index}`"
            :href="`tel:${phone}`"
            class="phone_card"
          >
            <span class="phone_card__icon">
              <i class="fas fa-phone"></i>
            </span>
            <span class="phone_card__info">
              <span class="phone_card__label">{{ $t("STATIC_PAGES.callUs") }}</span>
              <span class="phone_card__number" dir="ltr">{{ phone }}</span>
            </span>
            <i class="fas fa-chevron-left phone_card__arrow"></i>
          </a>
        </div>
      </section>

      <div
        v-if="!visibleSocialLinks.length && !visiblePhones.length"
        class="empty_state"
      >
        <i class="fas fa-inbox empty_state__icon"></i>
        <p>{{ $t("STATIC_PAGES.noContactInfo") }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import { fetchContactUs } from "@/services/generalApi";
import StaticPageLoader from "@/components/general/StaticPageLoader.vue";

const SOCIAL_FIELDS = [
  {
    key: "facebook",
    icon: "fab fa-facebook-f",
    label: "Facebook",
  },
  {
    key: "whatsapp",
    icon: "fab fa-whatsapp",
    label: "WhatsApp",
    buildUrl: (value) => {
      const digits = String(value).replace(/\D/g, "");
      return digits ? `https://wa.me/${digits}` : null;
    },
  },
  {
    key: "snapchat",
    icon: "fab fa-snapchat-ghost",
    label: "Snapchat",
  },
  {
    key: "instagram",
    icon: "fab fa-instagram",
    label: "Instagram",
  },
  {
    key: "tiktok",
    icon: "fab fa-tiktok",
    label: "TikTok",
  },
  {
    key: "x",
    icon: "fab fa-x-twitter",
    label: "X",
  },
];

export default {
  name: "SocialContactDisplay",

  components: {
    StaticPageLoader,
  },

  props: {
    contactData: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      contacts: {},
      loading: false,
    };
  },

  computed: {
    visibleSocialLinks() {
      return SOCIAL_FIELDS.reduce((links, field) => {
        const rawValue = this.contacts[field.key];
        if (!rawValue) {
          return links;
        }

        const url = field.buildUrl ? field.buildUrl(rawValue) : rawValue;
        if (!url) {
          return links;
        }

        links.push({
          key: field.key,
          icon: field.icon,
          label: field.label,
          url,
        });

        return links;
      }, []);
    },

    visiblePhones() {
      const phones = this.contacts.mobile || this.contacts.phones || [];
      return Array.isArray(phones)
        ? phones.filter((phone) => String(phone).trim())
        : [];
    },
  },

  watch: {
    contactData: {
      immediate: true,
      handler(value) {
        if (value) {
          this.contacts = value;
        }
      },
    },
  },

  methods: {
    async loadContacts() {
      if (this.contactData) {
        this.loading = false;
        return;
      }

      this.loading = true;

      try {
        this.contacts = await fetchContactUs();
      } catch (error) {
        console.error(
          "Error fetching contact info:",
          error.response?.data || error.message
        );
      } finally {
        this.loading = false;
      }
    },
  },

  created() {
    this.loadContacts();
  },
};
</script>

<style lang="scss" scoped>
.social_contact_display {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.contact_section {
  background: var(--theme_main_bg_clr);
  border: 1px solid var(--border_clr);
  border-radius: 14px;
  padding: 1.25rem;
}

.section_header {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 1rem;

  h3 {
    margin: 0;
    font-size: 1.05rem;
    color: var(--theme_text_clr);
    font-weight: 700;
  }
}

.section_icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--soft_main_theme_clr);
  color: var(--main_theme_clr);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
}

.social_grid,
.phones_grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.85rem;
}

.social_card,
.phone_card {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.9rem 1rem;
  border-radius: 12px;
  background: var(--theme_second_bg_clr);
  border: 1px solid var(--border_clr);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px var(--simple_shadow_clr);
    border-color: var(--main_theme_clr);
  }
}

.social_card__icon,
.phone_card__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  color: var(--white_clr);
  flex-shrink: 0;
}

.social_card__info,
.phone_card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.social_card__label,
.phone_card__label {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--theme_text_clr);
}

.social_card__hint {
  font-size: 0.8rem;
  color: var(--theme_secondary_text_clr);
}

.phone_card__number {
  font-size: 1rem;
  font-weight: 700;
  color: var(--main_theme_clr);
}

.social_card__arrow,
.phone_card__arrow {
  color: var(--light_gray_clr);
  font-size: 0.85rem;
  flex-shrink: 0;
}

.social_card--facebook .social_card__icon {
  background: var(--facebook_clr);
}

.social_card--whatsapp .social_card__icon {
  background: var(--whatsApp_clr);
}

.social_card--snapchat .social_card__icon {
  background: #fffc00;
  color: #000;
}

.social_card--instagram .social_card__icon {
  background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
}

.social_card--tiktok .social_card__icon {
  background: #010101;
}

.social_card--x .social_card__icon {
  background: #000;
}

.phone_card__icon {
  background: var(--main_theme_clr);
}

.empty_state {
  text-align: center;
  padding: 2.5rem 1rem;
  color: var(--theme_secondary_text_clr);

  &__icon {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-size: 0.95rem;
  }
}

[dir="ltr"] {
  .phone_card__arrow {
    transform: rotate(180deg);
  }
}
</style>
